import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  DetailsList as FluentDetailsList,
  DetailsRow,
  SelectionMode,
} from 'office-ui-fabric-react/lib/DetailsList';
import { ShimmeredDetailsList as FluentShimmeredDetailsList } from 'office-ui-fabric-react/lib/ShimmeredDetailsList';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import styles from './DetailsList.style';
import useBottomScrollListener from '../../hooks/useBottomScrollListener';

/**
 * An extension around Fluent UI's DetailsList to add functionality. When building lists in this
 * application
 *
 * @param {boolean} shimmered - Whether or not it should use Fluents Shimmered detail list.
 * @param {boolean} disableRowLink - By default, if your items have a `link` property, it will link the entire row
 */
const DetailsList = ({
  shimmered,
  disableRowLink,
  onRenderRow,
  checkboxVisibility,
  className: classNameProp,
  noBorderBottom,
  dataAutomationId,
  greyList,
  multiLine,
  compactRows,
  onScrollBottom,
  ...other
}) => {
  const className = clsx(styles.root, classNameProp, '', noBorderBottom && styles.noBorderBottom);
  const rowClassName = clsx(
    styles.transparentBackground,
    compactRows && styles.compactRow,
    !disableRowLink && styles.linkedRow,
    greyList && styles.greyRow,
    multiLine && styles.multiLineRow,
  );
  const ListComponent = shimmered ? FluentShimmeredDetailsList : FluentDetailsList;
  const history = useHistory();
  const [listElement, setListElement] = useState();
  const componentRefCallback = useCallback(
    ref => {
      // eslint-disable-next-line no-underscore-dangle
      setListElement(ref?._scrollElement);
    },
    [setListElement],
  );

  useBottomScrollListener(listElement, onScrollBottom);

  const CustomRow = props => {
    const handleRowClick = link => {
      if (!disableRowLink && link) {
        const { key, contractId, isOpenInNewTab } = props?.item;
        if ((key && contractId && key === `contract-${contractId}`) || isOpenInNewTab) {
          window.open(link);
        } else {
          history.push(link);
        }
      }
    };

    const handleKeyDown = (event, link) => {
      const eventAction = event.nativeEvent.keyCode;
      if (eventAction === 13 || eventAction === 32) {
        // 13 == enter
        // 32 == space
        handleRowClick(link);
      }
    };
    const getRoleDescription = () => {
      return !disableRowLink && props.item.link ? 'link' : 'row';
    };
    return (
      <DetailsRow
        {...props}
        onClick={() => handleRowClick(props.item.link)}
        onKeyDown={event => handleKeyDown(event, props.item.link)}
        className={props?.item?.isSelected ? clsx(rowClassName, styles.greyRow) : rowClassName}
        selectionMode={SelectionMode.none}
        data-testid={props.item.dataAutomationId || 'detail-row'}
        aria-roledescription={getRoleDescription()}
      />
    );
  };

  CustomRow.propTypes = {
    item: PropTypes.shape({
      link: PropTypes.string,
      dataAutomationId: PropTypes.string,
      isSelected: PropTypes.bool,
    }).isRequired,
  };

  return (
    <ListComponent
      listProps={{
        componentRef: componentRefCallback,
      }}
      checkboxVisibility={checkboxVisibility}
      onRenderRow={onRenderRow || CustomRow}
      className={className}
      styles={styles.list}
      selectionMode={SelectionMode.none}
      {...other}
    />
  );
};

DetailsList.propTypes = {
  shimmered: PropTypes.bool,
  disableRowLink: PropTypes.bool,
  onRenderRow: PropTypes.func,
  checkboxVisibility: PropTypes.number,
  className: PropTypes.string,
  dataAutomationId: PropTypes.string,
  noBorderBottom: PropTypes.bool,
  greyList: PropTypes.bool,
  multiLine: PropTypes.bool,
  compactRows: PropTypes.bool,
  onScrollBottom: PropTypes.func,
};

DetailsList.defaultProps = {
  shimmered: false,
  disableRowLink: false,
  onRenderRow: null,
  checkboxVisibility: 2,
  className: '',
  dataAutomationId: 'detail-row',
  noBorderBottom: false,
  greyList: false,
  multiLine: false,
  compactRows: true,
  onScrollBottom: null,
};

export default DetailsList;
